import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { Video, Loader2, History, Calendar, Trash2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

interface MeetingTranscriptionDialogProps {
  showDialog: boolean;
  onOpenChange: (open: boolean) => void;
  noteId: string | null;
  onTranscriptionComplete: (text: string, transcriptId?: string) => boolean | void | Promise<boolean | void>;
}

interface MeetingTranscription {
  id: string;
  meeting_url: string;
  provider: string;
  status: string;
  transcript_text: string | null;
  created_at: string;
}

const MeetingTranscriptionDialog: React.FC<MeetingTranscriptionDialogProps> = ({
  showDialog,
  onOpenChange,
  noteId,
  onTranscriptionComplete,
}) => {
  const [meetingUrl, setMeetingUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pastTranscripts, setPastTranscripts] = useState<MeetingTranscription[]>([]);
  const [isLoadingTranscripts, setIsLoadingTranscripts] = useState(false);
  const { toast } = useToast();

  const detectMeetingProvider = (url: string): string => {
    if (url.includes('meet.google.com')) return 'Google Meet';
    if (url.includes('zoom.us') || url.includes('zoom.com')) return 'Zoom';
    if (url.includes('teams.microsoft.com')) return 'Microsoft Teams Business';
    if (url.includes('teams.live.com')) return 'Microsoft Teams Personal';
    return 'Unknown';
  };

  const fetchPastTranscripts = async (fetchAll: boolean = false) => {
    setIsLoadingTranscripts(true);
    try {
      let query = supabase
        .from('meeting_transcriptions')
        .select('*')
        .order('created_at', { ascending: false });

      if (!fetchAll) {
        query = query.limit(10);
      }

      const { data, error } = await query;

      if (error) throw error;
      setPastTranscripts(data || []);
    } catch (error) {
      console.error('Error fetching past transcripts:', error);
      toast({
        title: "Failed to load transcripts",
        description: "Could not load past meeting transcripts",
        variant: "destructive",
      });
    } finally {
      setIsLoadingTranscripts(false);
    }
  };

  // Silently auto-finalize any "failed" or long-stuck "transcribing" jobs.
  // Recall.ai sometimes leaves jobs in those states even when the recording
  // is finished — historically users had to click "Force Completion" by hand.
  // Now we do it automatically so they only see "completed" when ready.
  const autoFinalizeRef = useRef<{ inFlight: boolean; lastRun: number }>({ inFlight: false, lastRun: 0 });
  const maybeAutoFinalize = async (transcripts: MeetingTranscription[]) => {
    if (autoFinalizeRef.current.inFlight) return;
    // Throttle to at most once every 15s so short calls get unstuck quickly
    if (Date.now() - autoFinalizeRef.current.lastRun < 15_000) return;

    const needsFinalize = transcripts.some((t) => {
      if (t.status === 'failed') return true;
      if (t.status === 'transcribing' || t.status === 'pending') {
        const ageMs = Date.now() - new Date(t.created_at).getTime();
        // Nudge after 45s — Recall.ai often leaves short calls in "transcribing"
        // because bot.done webhook arrives late or not at all.
        return ageMs > 45 * 1000;
      }
      return false;
    });
    if (!needsFinalize) return;

    autoFinalizeRef.current.inFlight = true;
    autoFinalizeRef.current.lastRun = Date.now();
    try {
      await supabase.functions.invoke('complete-stuck-transcriptions', { body: { force: true } });
      // Refresh once finalization completes so the UI reflects new statuses
      await fetchPastTranscripts();
    } catch (e) {
      console.warn('Auto-finalize failed (will retry on next poll):', e);
    } finally {
      autoFinalizeRef.current.inFlight = false;
    }
  };

  // Run auto-finalize whenever the transcripts list updates
  useEffect(() => {
    if (!showDialog) return;
    if (pastTranscripts.length === 0) return;
    maybeAutoFinalize(pastTranscripts);
  }, [pastTranscripts, showDialog]);

  useEffect(() => {
    if (!showDialog) return;
    fetchPastTranscripts();
    // While dialog is open, keep polling so short calls finalize promptly
    const interval = setInterval(() => {
      fetchPastTranscripts();
    }, 10_000);
    return () => clearInterval(interval);
  }, [showDialog]);

  const handleStartTranscription = async () => {
    if (!meetingUrl.trim()) {
      toast({
        title: "Meeting URL required",
        description: "Please enter a valid meeting URL",
        variant: "destructive",
      });
      return;
    }

    const provider = detectMeetingProvider(meetingUrl);
    if (provider === 'Unknown') {
      toast({
        title: "Unsupported meeting provider",
        description: "Please use Google Meet, Zoom, or Microsoft Teams links",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('meeting-transcription', {
        body: {
          meeting_url: meetingUrl,
          note_id: noteId,
          provider: provider
        }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Bot sent to meeting",
        description: `Mindnote bot has been sent to your ${provider} meeting. It may take 1-2 minutes to join the call. Transcription will appear automatically in your note when the meeting ends.`,
        duration: 8000,
      });

      setMeetingUrl("");
      fetchPastTranscripts(); // Refresh the list
    } catch (error) {
      console.error('Error starting transcription:', error);
      toast({
        title: "Failed to start transcription",
        description: "There was an error sending the bot to your meeting. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addingRef = useRef(false);
  const [addingTranscriptId, setAddingTranscriptId] = useState<string | null>(null);
  const handleAddTranscript = async (transcript: MeetingTranscription) => {
    if (addingRef.current || addingTranscriptId) return; // guard against duplicate event delivery
    addingRef.current = true;
    setAddingTranscriptId(transcript.id);

    try {
      const { data, error } = await supabase
        .from('meeting_transcriptions')
        .select('*')
        .eq('id', transcript.id)
        .maybeSingle();

      if (error) throw error;

      const selectedTranscript = (data || transcript) as MeetingTranscription;
      if (!selectedTranscript.transcript_text) return;

      const cleanedText = cleanTranscriptText(selectedTranscript.transcript_text);
      if (!cleanedText) return;

      const wasAdded = (await onTranscriptionComplete(cleanedText, selectedTranscript.id)) !== false;
      onOpenChange(false);
      toast({
        title: wasAdded ? "Transcript added" : "Transcript already added",
        description: wasAdded
          ? "The selected transcript has been added to your note"
          : "This transcript is already in the current note",
      });
    } catch (error) {
      console.error('Error adding selected transcript:', error);
      toast({
        title: "Failed to add transcript",
        description: "Could not add the selected transcript. Please try again.",
        variant: "destructive",
      });
    } finally {
      // Release guard after dialog fully closes / async state settles
      setTimeout(() => {
        addingRef.current = false;
        setAddingTranscriptId(null);
      }, 1000);
    }
  };

  const handleDeleteTranscript = async (transcriptId: string) => {
    try {
      const { error } = await supabase
        .from('meeting_transcriptions')
        .delete()
        .eq('id', transcriptId);

      if (error) throw error;

      setPastTranscripts(prev => prev.filter(t => t.id !== transcriptId));
      toast({
        title: "Transcript deleted",
        description: "The transcript has been removed",
      });
    } catch (error) {
      console.error('Error deleting transcript:', error);
      toast({
        title: "Failed to delete",
        description: "Could not delete the transcript",
        variant: "destructive",
      });
    }
  };

  const handleCompleteStuckTranscriptions = async () => {
    setIsLoadingTranscripts(true);
    try {
      const { data, error } = await supabase.functions.invoke('complete-stuck-transcriptions', {
        body: { force: true }
      });

      if (error) throw error;

      const processed = data?.processed || 0;
      const completed = data?.completed || 0;
      
      toast({
        title: "Processing complete",
        description: `Processed ${processed} transcriptions, completed ${completed}. Refreshing transcript list...`,
      });

      // Refresh the transcripts after processing
      await fetchPastTranscripts(true);
    } catch (error) {
      console.error('Error completing stuck transcriptions:', error);
      toast({
        title: "Failed to complete transcriptions",
        description: "Could not process stuck transcriptions",
        variant: "destructive",
      });
    } finally {
      setIsLoadingTranscripts(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const cleanTranscriptText = (text: string): string => {
    const normalizedInput = text
      .replace(/\r\n/g, '\n')
      .replace(/<!--\s*mindnote-transcript:[^>]*-->/gi, '')
      .replace(/\*\*/g, '')
      .trim();
    const blocks = normalizedInput
      .split(/(?:^|\n+)\s*(?:#{1,6}\s*)?(?:Meeting|Final) Transcript\b\s*/i)
      .map((part) => part.trim())
      .filter(Boolean);

    const normalizeBlock = (value: string) => value
      .replace(/^\s*#{1,6}\s*(?:Meeting|Final) Transcript\b\s*/gim, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    const selectedBlock = (() => {
      if (blocks.length <= 1) return blocks[0] || normalizedInput;

      // Some older transcript rows were polluted with multiple note-level
      // "Meeting Transcript" sections. Pick one section only: if a section is
      // repeated, that is the actual duplicated transcript; otherwise use the
      // first section stored on that row rather than appending the whole bundle.
      const counts = new Map<string, number>();
      blocks.forEach((block) => {
        const key = normalizeBlock(block);
        if (key) counts.set(key, (counts.get(key) || 0) + 1);
      });
      const repeated = [...counts.entries()].sort((a, b) => b[1] - a[1]).find(([, count]) => count > 1)?.[0];
      return repeated ? blocks.find((block) => normalizeBlock(block) === repeated) || blocks[0] : blocks[0];
    })();

    const cleanedBlock = selectedBlock
      .replace(/^\s*#{1,6}\s*(?:Meeting|Final) Transcript\s*$/gim, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return cleanedBlock ? `## Meeting Transcript\n\n${cleanedBlock}` : '';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600';
      case 'pending': return 'text-yellow-600';
      case 'transcribing': return 'text-blue-600';
      case 'failed': return 'text-amber-600';
      default: return 'text-gray-600';
    }
  };

  // Friendly label: "failed" is usually just "needs finalization" in practice,
  // because the bot finished but the webhook didn't auto-close the job.
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'failed': return 'Finalizing…';
      case 'transcribing': return 'transcribing';
      case 'pending': return 'pending';
      case 'completed': return 'completed';
      default: return status;
    }
  };

  return (
    <Dialog open={showDialog} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            Meeting Transcription (Beta)
          </DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="new" className="w-full">
          <TabsList className="grid w-full grid-cols-2 h-auto gap-1">
            <TabsTrigger value="new" className="flex items-center justify-center gap-1.5 text-xs sm:text-sm px-2 py-2 whitespace-nowrap min-w-0">
              <Video className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
              <span className="truncate">New Meeting</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center justify-center gap-1.5 text-xs sm:text-sm px-2 py-2 whitespace-nowrap min-w-0">
              <History className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
              <span className="truncate">Past Transcripts</span>
            </TabsTrigger>
          </TabsList>

          
          <TabsContent value="new" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="meeting-url">Meeting URL</Label>
              <Input
                id="meeting-url"
                placeholder="Paste your Google Meet, Zoom, or Teams meeting link..."
                value={meetingUrl}
                onChange={(e) => setMeetingUrl(e.target.value)}
                disabled={isLoading}
              />
            </div>
            
            <div className="text-sm text-muted-foreground space-y-3">
              <div>
                <p className="font-medium mb-1">Supported platforms:</p>
                <ul className="space-y-1">
                  <li>• Google Meet</li>
                  <li>• Zoom</li>
                  <li>• Microsoft Teams (Business)</li>
                  <li>• Microsoft Teams (Personal)</li>
                </ul>
              </div>
              <div className="bg-muted border border-border rounded-md p-3">
                <p className="font-medium text-foreground mb-1 text-sm">Important notes:</p>
                <ul className="text-muted-foreground space-y-1 text-xs">
                  <li>• The bot will take 1-2 minutes to join your meeting</li>
                  <li>• Transcription typically completes within 5-10 minutes after meeting ends</li>
                  <li>• No limit on daily meetings - record as many as needed</li>
                  <li>• Meeting recording supports up to 2 hours</li>
                  <li>• For Zoom: Please enable automatic captions in your meeting settings</li>
                  <li>• Currently only English is supported</li>
                  <li>• This feature is not yet HIPAA-compliant. While compliance improvements are in progress, it's best used for non-sensitive content such as webinars, open lectures, or public-facing meetings. Be mindful of any confidential data shared.</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleStartTranscription} 
                disabled={isLoading || !meetingUrl.trim()}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending Bot...
                  </>
                ) : (
                  'Send Bot to Meeting'
                )}
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="history" className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Past Meeting Transcripts</h3>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleCompleteStuckTranscriptions} 
                    disabled={isLoadingTranscripts}
                    title="Use if transcription is stuck in 'transcribing' status for more than 5-10 minutes"
                  >
                    {isLoadingTranscripts ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Force Completion'
                    )}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => fetchPastTranscripts()} disabled={isLoadingTranscripts}>
                    {isLoadingTranscripts ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Refresh'
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Transcriptions typically complete within 5-10 minutes after your meeting ends.
                Jobs that show <span className="font-medium text-amber-600">"Finalizing…"</span> are auto-finalized in the background — no action needed. If something stays stuck for a while, you can also click <span className="font-medium">"Force Completion"</span> to nudge it manually.
              </p>
            </div>
            
            <ScrollArea className="h-[400px]">
              {pastTranscripts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No past transcripts found</p>
                  <p className="text-sm">Start a meeting transcription to see it here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pastTranscripts.map((transcript) => (
                    <div key={transcript.id} className="border rounded-lg p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {formatDate(transcript.created_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${getStatusColor(transcript.status)}`}>
                            {getStatusLabel(transcript.status)}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTranscript(transcript.id)}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="text-sm">
                        <p className="font-medium">{transcript.provider}</p>
                        <p className="text-muted-foreground text-xs break-all">{transcript.meeting_url}</p>
                      </div>

                      {transcript.status === 'failed' && (
                        <div className="text-xs bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 rounded-md p-2">
                          We're auto-finalizing this recording in the background — it should appear as completed shortly. No action needed on your side.
                        </div>
                      )}
                      
                      {transcript.status === 'completed' && (
                        <>
                          <Separator />
                          {transcript.transcript_text ? (
                            <div className="text-sm">
                              <p className="text-muted-foreground mb-1">Transcript preview:</p>
                              <p className="line-clamp-3">{cleanTranscriptText(transcript.transcript_text).substring(0, 200)}...</p>
                            </div>
                          ) : (
                            <div className="text-sm">
                              <p className="text-muted-foreground text-xs">Transcript completed but text not available. Try "Force Completion" to retrieve it.</p>
                            </div>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleAddTranscript(transcript)}
                            className="w-full"
                            disabled={!transcript.transcript_text || Boolean(addingTranscriptId)}
                          >
                            {addingTranscriptId === transcript.id
                              ? 'Adding...'
                              : transcript.transcript_text
                                ? 'Add to Current Note'
                                : 'Transcript Not Available'}
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default MeetingTranscriptionDialog;